import { getApiClient } from './client';
import { getShopId } from '../utils/shopId';
import type {
  Product,
  ProductListResponse,
  ProductListParams,
  GenerateAssetRequest,
  UpdateAssetSettingsRequest,
  CreateGenerationRequestV2,
  GenerationRunResponseV2,
  EditorStateResponse,
  EditorZone,
  ZoneOverride,
  OrientationOverride,
  PlacementContract,
} from '../types/product.types';

export const productsApi = {
  list: async (params?: ProductListParams): Promise<ProductListResponse> => {
    const client = getApiClient();
    const shopId = getShopId();
    const queryParams = shopId ? { ...params, shop_id: shopId } : params;
    console.log('[productsApi.list] Calling /api/products with params:', queryParams, 'shopId:', shopId);
    try {
      const response = await client.get<ProductListResponse>('/api/products', { params: queryParams });
      console.log('[productsApi.list] Response status:', response.status);
      console.log('[productsApi.list] Response data:', response.data);
      console.log('[productsApi.list] Products count:', response.data?.data?.length || 0);
      console.log('[productsApi.list] Total products:', response.data?.pagination?.total || 0);
      return response.data;
    } catch (error: any) {
      console.error('[productsApi.list] Error fetching products:', error);
      console.error('[productsApi.list] Error response:', error?.response?.data);
      console.error('[productsApi.list] Error status:', error?.response?.status);
      throw error;
    }
  },

  get: async (id: string): Promise<Product> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.get<Product>(`/api/products/${id}`, { params });
    return data;
  },

  getImages: async (id: string): Promise<string[]> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.get<string[]>(`/api/products/${id}/images`, { params });
    return data;
  },

  sync: async (): Promise<{ success: boolean; synced: number; created: number; updated: number }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.post('/api/products/sync', undefined, { params });
    return data;
  },
};

export const assetsApi = {
  generate: async (productId: string, request: GenerateAssetRequest = {}): Promise<{ id: string; status: string }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId 
      ? `/api/products/${productId}/assets/generate?shop_id=${shopId}`
      : `/api/products/${productId}/assets/generate`;
    
    const { data } = await client.post(url, request);
    return data;
  },

  updateSettings: async (
    productId: string,
    request: UpdateAssetSettingsRequest
  ): Promise<{ id: string; status: string }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/products/${productId}/assets/settings?shop_id=${shopId}`
      : `/api/products/${productId}/assets/settings`;
    const { data } = await client.post(url, request);
    return data;
  },

  cancelGeneration: async (productId: string): Promise<{ asset_id: string; status: string; message: string }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/products/${productId}/assets/generate?shop_id=${shopId}`
      : `/api/products/${productId}/assets/generate`;
    const { data } = await client.delete(url);
    return data;
  },

  delete: async (productId: string): Promise<{ product_id: string; message: string }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/products/${productId}/assets?shop_id=${shopId}`
      : `/api/products/${productId}/assets`;
    const { data } = await client.delete(url);
    return data;
  },
};

export const generationsApi = {
  create: async (
    request: CreateGenerationRequestV2
  ): Promise<GenerationRunResponseV2> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/v2/generations?shop_id=${shopId}`
      : `/api/v2/generations`;
    const { data } = await client.post(url, request);
    return data;
  },

  get: async (runId: string): Promise<GenerationRunResponseV2> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/v2/generations/${runId}?shop_id=${shopId}`
      : `/api/v2/generations/${runId}`;
    const { data } = await client.get(url);
    return data;
  },

  cancel: async (
    runId: string
  ): Promise<{ run_id: string; status: string; current_stage?: string | null }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/v2/generations/${runId}/cancel?shop_id=${shopId}`
      : `/api/v2/generations/${runId}/cancel`;
    const { data } = await client.post(url);
    return data;
  },

  promoteMaster: async (
    runId: string
  ): Promise<{ run_id: string; product_asset_id: string; glb_url: string; status: string }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/v2/generations/${runId}/promote-master?shop_id=${shopId}`
      : `/api/v2/generations/${runId}/promote-master`;
    const { data } = await client.post(url);
    return data;
  },
};

// ── AR Config API ─────────────────────────────────────────────────────────────

export interface ARConfig {
  placement_type: string | null;
  ar_engine_type: string | null;
  accessory_type: string | null;
  published_runtime: string | null;
  pack_id: string | null;
  fit_profile_json: Record<string, unknown> | null;
  world_dimensions_json: Record<string, unknown> | null;
  ar_fallback_mode: string;
  ar_is_published: boolean;
  last_ar_publish_at: string | null;
}

export interface UpdateARConfigRequest {
  placement_type: string;
  accessory_type?: string | null;
  fit_profile_json?: Record<string, unknown> | null;
  world_dimensions_json?: Record<string, unknown> | null;
  fallback_mode?: string;
  /** Required when placement_type === 'marker'. URL to the compiled .mind target file. */
  mind_target_url?: string | null;
}

export const arConfigApi = {
  get: async (productId: string, assetId: string): Promise<ARConfig> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.get<ARConfig>(
      `/api/products/${productId}/assets/${assetId}/ar-config`,
      { params },
    );
    return data;
  },

  save: async (
    productId: string,
    assetId: string,
    config: UpdateARConfigRequest,
  ): Promise<ARConfig> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.post<ARConfig>(
      `/api/products/${productId}/assets/${assetId}/ar-config`,
      config,
      { params },
    );
    return data;
  },

  publish: async (productId: string, assetId: string): Promise<ARConfig> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.post<ARConfig>(
      `/api/products/${productId}/assets/${assetId}/ar-publish`,
      undefined,
      { params },
    );
    return data;
  },
};

// ── Asset Editor API ───────────────────────────────────────────────────────────

export interface SaveEditorDraftRequest {
  manifest: {
    version?: number;
    /** Omit to keep server zones (e.g. when enabling split — backend recomputes). */
    zones?: EditorZone[];
    draftOverrides: Record<string, ZoneOverride>;
    publishedOverrides?: Record<string, ZoneOverride>;
    splitBySide?: boolean;
    orientationOverride?: OrientationOverride;
    /** Optional baked placement contract; backend validates defensively and persists it in the editor manifest. */
    placement_contract?: PlacementContract | null;
  };
}

export const assetEditorApi = {
  getState: async (productId: string): Promise<EditorStateResponse> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.get<EditorStateResponse>(
      `/api/products/${productId}/assets/editor`,
      { params },
    );
    return data;
  },

  saveDraft: async (
    productId: string,
    request: SaveEditorDraftRequest,
  ): Promise<EditorStateResponse> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.post<EditorStateResponse>(
      `/api/products/${productId}/assets/editor/draft`,
      request,
      { params },
    );
    return data;
  },

  publish: async (productId: string): Promise<EditorStateResponse> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.post<EditorStateResponse>(
      `/api/products/${productId}/assets/editor/publish`,
      undefined,
      { params },
    );
    return data;
  },

  /** Multipart field name: `file` — binary GLB only. */
  uploadCustomGlb: async (productId: string, file: File): Promise<EditorStateResponse> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const form = new FormData();
    form.append('file', file);
    const { data } = await client.post<EditorStateResponse>(
      `/api/products/${productId}/assets/editor/custom-glb`,
      form,
      { params },
    );
    return data;
  },

  reset: async (productId: string): Promise<EditorStateResponse> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.post<EditorStateResponse>(
      `/api/products/${productId}/assets/editor/reset`,
      undefined,
      { params },
    );
    return data;
  },

  getPreviewGlbBlobUrl: async (productId: string): Promise<string> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = shopId ? { shop_id: shopId } : undefined;
    const { data } = await client.get<ArrayBuffer>(
      `/api/products/${productId}/assets/editor/preview-glb`,
      { responseType: 'arraybuffer', params },
    );
    const blob = new Blob([data], { type: 'model/gltf-binary' });
    return URL.createObjectURL(blob);
  },

  fetchGlbBlobUrl: async (productId: string, assetId: string): Promise<string> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = {
      ...(shopId ? { shop_id: shopId } : {}),
      _cb: Date.now(),
    };
    const { data } = await client.get<ArrayBuffer>(
      `/api/products/${productId}/assets/${assetId}/glb`,
      { responseType: 'arraybuffer', params },
    );
    const blob = new Blob([data], { type: 'model/gltf-binary' });
    return URL.createObjectURL(blob);
  },
};
